// 菜单权限管理工具函数

/**
 * 根据用户权限过滤菜单
 * @param {Array} menuList - 原始菜单列表
 * @param {Array} userPermissions - 用户权限列表
 * @returns {Array} - 过滤后的菜单列表
 */
export function filterMenusByPermissions(menuList, userPermissions) {
  // 如果权限列表为空，检查菜单的 isSelect 字段
  if (!userPermissions || userPermissions.length === 0) {
    return menuList.filter(menu => {
      // 如果菜单的 isSelect 为 1，则显示
      const isSelected = menu.isSelect === 1 || menu.isSelect === undefined
      
      if (isSelected && menu.children && menu.children.length > 0) {
        // 如果有子菜单，递归过滤子菜单
        menu.children = filterMenusByPermissions(menu.children, userPermissions)
        // 如果过滤后还有子菜单，则保留此菜单
        // 如果父菜单的 isSelect 为 1，即使子菜单被过滤后为空，也显示父菜单（但子菜单为空）
        if (menu.children.length > 0) {
          return true
        }
        // 如果子菜单被过滤后为空，但父菜单的 isSelect 为 1，也显示父菜单
        return isSelected
      }
      
      return isSelected
    })
  }

  return menuList.filter(menu => {
    // 检查菜单是否有权限，或者菜单的 isSelect 为 1
    const hasPermission = userPermissions.includes(menu.menuCode)
    const isSelected = menu.isSelect === 1 || menu.isSelect === undefined
    
    // 如果有权限或 isSelect 为 1，则显示
    const shouldShow = hasPermission || isSelected
    
    if (shouldShow && menu.children && menu.children.length > 0) {
      // 如果有权限且有子菜单，递归过滤子菜单
      menu.children = filterMenusByPermissions(menu.children, userPermissions)
      // 如果过滤后还有子菜单，则保留此菜单
      // 如果父菜单的 isSelect 为 1，即使子菜单被过滤后为空，也显示父菜单（但子菜单为空）
      if (menu.children.length > 0) {
        return true
      }
      // 如果子菜单被过滤后为空，但父菜单的 isSelect 为 1，也显示父菜单
      return isSelected
    }
    
    return shouldShow
  })
}

/**
 * 根据用户角色获取菜单权限
 * @param {Object} userInfo - 用户信息
 * @returns {Array} - 权限列表
 */
export function getUserMenuPermissions(userInfo) {
  if (!userInfo) return []
  
  // 根据用户信息中的权限字段获取权限列表
  // 这里需要根据实际API返回的数据结构来调整
  const permissions = []
  
  // 如果用户信息中有菜单权限列表
  if (userInfo.menuPermissions && Array.isArray(userInfo.menuPermissions)) {
    return userInfo.menuPermissions
  }
  
  // 如果用户信息中有菜单列表（从getU API返回）
  if (userInfo.menus && Array.isArray(userInfo.menus)) {
    return userInfo.menus.map(menu => menu.menuCode)
  }
  
  // 如果用户信息中有角色信息
  if (userInfo.roles && Array.isArray(userInfo.roles)) {
    // 根据角色映射权限
    userInfo.roles.forEach(role => {
      const rolePermissions = getRolePermissions(role)
      permissions.push(...rolePermissions)
    })
  }
  
  // 如果用户信息中有权限字段（从getU API返回）
  if (userInfo.permissions && Array.isArray(userInfo.permissions)) {
    return userInfo.permissions
  }
  
  // 如果用户信息中有isSelect字段的菜单（从getU API返回）
  if (userInfo.responseMenus && Array.isArray(userInfo.responseMenus)) {
    return extractPermissionsFromMenus(userInfo.responseMenus)
  }
  
  return permissions
}

/**
 * 从菜单列表中提取权限
 * @param {Array} menus - 菜单列表
 * @returns {Array} - 权限列表
 */
function extractPermissionsFromMenus(menus) {
  const permissions = []
  
  menus.forEach(menu => {
    if (menu.isSelect === 1) {
      permissions.push(menu.menuCode)
    }
    
    if (menu.children && Array.isArray(menu.children)) {
      const childPermissions = extractPermissionsFromMenus(menu.children)
      permissions.push(...childPermissions)
    }
  })
  
  return permissions
}

/**
 * 根据角色获取权限列表
 * @param {String} role - 角色名称
 * @returns {Array} - 权限列表
 */
function getRolePermissions(role) {
  const rolePermissionMap = {
    'admin': [
      'INDEX', 'DATAM', 'EQUM', 'LVXM', 'UIVERSION', 'DATAQ', 'HEART', 'CUSTOM', 'EQUACT',
      'CRMM', 'ORGM', 'AGENTCHECK', 'AGENTCPS', 'AGENTROUTE', 'PARTY_A',
      'MQTTM', 'STATUSM', 'OUTORDERM', 'DEBUGER', 'WATERTTL',
      'ACCTM', 'ACCTMA', 'TYPEM', 'SETM', 'VOLMAP', 'WATER_CONFIG', 'BUSSLOG',
      'WX_PAY', 'WX_PAY_ORDER', 'WX_PAY_STAT', 'WX_PAY_WATER_CONFIG', 'WX_PAY_WATER_RECHARGE'
    ],
    'manager': [
      'INDEX', 'DATAM', 'EQUM', 'LVXM', 'UIVERSION', 'DATAQ', 'HEART', 'CUSTOM', 'EQUACT',
      'CRMM', 'ORGM', 'AGENTCHECK', 'AGENTCPS', 'AGENTROUTE', 'PARTY_A',
      'MQTTM', 'STATUSM', 'OUTORDERM', 'DEBUGER', 'WATERTTL'
    ],
    'operator': [
      'INDEX', 'DATAM', 'EQUM', 'LVXM', 'UIVERSION', 'DATAQ', 'HEART', 'CUSTOM', 'EQUACT'
    ],
    'viewer': [
      'INDEX', 'DATAM', 'EQUM', 'DATAQ', 'HEART', 'CUSTOM', 'EQUACT'
    ]
  }
  
  return rolePermissionMap[role] || []
}

/**
 * 检查用户是否有特定菜单权限
 * @param {String} menuCode - 菜单代码
 * @param {Array} userPermissions - 用户权限列表
 * @returns {Boolean} - 是否有权限
 */
export function hasMenuPermission(menuCode, userPermissions, menuItem = null) {
  // 如果权限列表为空，检查菜单项的 isSelect 字段
  if (!userPermissions || !Array.isArray(userPermissions) || userPermissions.length === 0) {
    // 如果提供了菜单项，检查 isSelect 字段
    if (menuItem) {
      return menuItem.isSelect === 1 || menuItem.isSelect === undefined
    }
    // 如果没有提供菜单项，返回 true（允许显示，由其他逻辑控制）
    return true
  }
  
  return userPermissions.includes(menuCode)
}

/**
 * 获取用户可见的菜单列表
 * @param {Array} allMenus - 所有菜单列表
 * @param {Object} userInfo - 用户信息
 * @returns {Array} - 用户可见的菜单列表
 */
export function getUserVisibleMenus(allMenus, userInfo) {
  const userPermissions = getUserMenuPermissions(userInfo)
  return filterMenusByPermissions(allMenus, userPermissions)
}
